import React, { FC, Fragment, useState } from 'react';
import { TableContainer, Table, TableHead, TableRow, TableCell, TableBody, Paper } from '@mui/material';
import { Dentist, User } from 'models/user';
import { Organization } from 'models/organization';
import { useTranslation } from 'react-i18next';
import InfiniteScroll from 'react-infinite-scroll-component';
import UpdateDentistDialog from 'components/users/dentists/update-dentist-dialog/update-dentist-dialog.component';
import DentistItem from 'components/users/dentists/dentist-list/dentist-item.component';
import DelayedLinearProgress from 'components/shared/delayed-linear-progress/delayed-linear-progress.component';
import useFixMissingScroll from 'utils/use-fix-missing-scroll';

type DentistListProps = {
    filteredDentists: User[];
    onFetchMore: () => void;
    dentistsLoading: boolean;
    onUserUpdated: () => void;
    onLoginClick?: (user: User) => void;
};
const DentistList: FC<DentistListProps> = ({
    filteredDentists,
    onFetchMore,
    dentistsLoading,
    onUserUpdated,
    onLoginClick,
}: DentistListProps) => {
    const { t: translate } = useTranslation(['common']);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dentistToUpdate, setDentistToUpdate] = useState<Dentist>({
        _id: '',
        title: '',
        firstName: '',
        lastName: '',
        email: '',
        role: '',
        authMethod: undefined,
        syncid: '',
        organization: '',
        defaultClinic: '',
        isActive: undefined,
        enableUserSync: false,
    });

    const handleUpdateDentist = (dentist: User) => {
        setDentistToUpdate({
            _id: dentist._id,
            title: dentist.title,
            firstName: dentist.name.firstName,
            lastName: dentist.name.lastName,
            email: dentist.email,
            role: dentist.role,
            authMethod: dentist.authMethod,
            syncid: dentist.syncid,
            personalIdentityNumber: dentist.personalIdentityNumber,
            organization: dentist.organization as Organization,
            defaultClinic: dentist.systemSetting?.clinic,
            isActive: dentist.active,
            enableUserSync: dentist.enableUserSync || false,
        });
        setDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
    };

    // Fix for infinite scroll not working on some screen sizes.
    useFixMissingScroll(onFetchMore, filteredDentists?.length || 0);

    return (
        <Fragment>
            {dentistsLoading ? <DelayedLinearProgress /> : undefined}
            <TableContainer component={Paper}>
                <InfiniteScroll
                    dataLength={filteredDentists ? filteredDentists.length : 0}
                    next={onFetchMore}
                    hasMore={true}
                    throttleTimeInMs={75}
                    loader={undefined}
                >
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell align="center">{translate('common:STATUS')}</TableCell>
                                <TableCell>{translate('common:NAME')}</TableCell>
                                <TableCell>{translate('common:EMAIL')}</TableCell>
                                <TableCell>{translate('common:ROLE')}</TableCell>
                                <TableCell>{translate('common:CLINIC')}</TableCell>
                                <TableCell></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredDentists ? (
                                <Fragment>
                                    {filteredDentists.map((dentist: User) => (
                                        <DentistItem
                                            key={dentist._id}
                                            dentist={dentist}
                                            onUpdateDentist={handleUpdateDentist}
                                            onLoginClick={onLoginClick}
                                        />
                                    ))}
                                </Fragment>
                            ) : undefined}
                        </TableBody>
                    </Table>
                </InfiniteScroll>
            </TableContainer>
            <UpdateDentistDialog
                dialogOpen={dialogOpen}
                onDialogClose={handleCloseDialog}
                dentistToUpdate={dentistToUpdate}
                onUserUpdated={onUserUpdated}
            />
        </Fragment>
    );
};

export default DentistList;
